import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { formatDate } from '@if/core/utils'
import { array, dateOnly, InferSubmit, mixed, number, object, text } from '@if/core/yup'
import { getToday } from '@src/services/DateTimeProvider'
import { EPaymentMethod, EProduct, PRODUCT_REFUND_PAYMENT_METHODS } from '@src/types/constants'
import { useMemo } from 'react'

/**
 * Mirrors the backend refund validator so the agent hears about a bad value before Confirm
 * does; the backend stays the judge, and its refusal keeps the dialog open with the error.
 * Which products can be refunded is the backend's call too: the dialog only offers the ones
 * it lists. The amount is only required to be positive: a partial refund is fine.
 */
export const getProductRefundSchema = (loanActivationDate: DateOnly) =>
  object({
    productIds: array(mixed<EProduct>().required()).required().min(1).default([]),
    amount: number().required().positive().default(null),
    effectiveDate: dateOnly()
      .required()
      .min(loanActivationDate, () =>
        t('loan.payment.productRefundDialog.errors.beforeActivation', { date: formatDate(loanActivationDate) }),
      )
      // Read at validation time: the schema outlives the day it was built on.
      .test(
        'effectiveDate-afterToday',
        () => t('loan.payment.productRefundDialog.errors.afterToday'),
        (value) => !value || value <= getToday(),
      )
      .default(null),
    paymentMethodId: mixed<EPaymentMethod>()
      .oneOf(PRODUCT_REFUND_PAYMENT_METHODS)
      .required()
      .default(EPaymentMethod.Cheque),
    chequeNumber: number()
      .integer()
      .positive()
      .nullable()
      .default(null)
      .when('paymentMethodId', { is: EPaymentMethod.Cheque, then: (schema) => schema.required() }),
    note: text().notRequired().default(''),
  })

export type ProductRefundSchemaType = InferSubmit<ReturnType<typeof getProductRefundSchema>>

export const useProductRefundSchema = (loanActivationDate: DateOnly) =>
  useMemo(() => getProductRefundSchema(loanActivationDate), [loanActivationDate])
